import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import {
	Add,
	Close,
	ExpandLess,
	ExpandMore,
	Restore,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	FormControlLabel,
	IconButton,
	InputBase,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getEmailRecipientsResources } from "../../repositories/CalendarRepository";

interface CustomEmail {
	id: string;
	email: string;
	selected: boolean;
}

const isValidEmail = (email: string): boolean => {
	if (!email.trim()) return true;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
};

type Props = {
	open: boolean;
	title?: string;
	message?: string;
	resource: string;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (selectedEmails: string[], publisherNote?: string) => void;
};

export const PublishCalendarModal = ({
	open,
	title = "Publish Calendar",
	message = "Are you sure you want to publish...",
	resource,
	loading = false,
	onClose,
	onConfirm,
}: Props) => {
	const defaultAreaId = useCalendarAreaId();
	const [notifyByEmail, setNotifyByEmail] = useState(true);
	const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
	const [hiddenEmails, setHiddenEmails] = useState<Set<string>>(new Set());
	const [expanded, setExpanded] = useState(true);
	const [customEmails, setCustomEmails] = useState<CustomEmail[]>([]);
	const [publisherNote, setPublisherNote] = useState("");
	const [error, setError] = useState("");

	const {
		data: emails = [],
		isLoading: isLoadingEmails,
		isSuccess,
		error: e,
		isError,
	} = useQuery({
		queryKey: ["emailRecipients", resource, defaultAreaId],
		queryFn: ({ signal }) =>
			getEmailRecipientsResources({ resource, defaultAreaId }, signal),
		enabled: open,
	});

	const updateNotifyByEmailState = (
		selected: Set<string>,
		custom: CustomEmail[],
	) => {
		const hasSelectedEmails = selected.size > 0;
		const hasSelectedCustom = custom.some((ce) => ce.selected && ce.email);
		setNotifyByEmail(hasSelectedEmails || hasSelectedCustom);
	};

	const handleNotifyToggle = () => {
		const newValue = !notifyByEmail;
		setNotifyByEmail(newValue);
		if (newValue) {
			setSelectedEmails(new Set(emails));
			setExpanded(true);
			// If no existing emails, automatically add a new email input, otherwise select all
			setCustomEmails((prev) => {
				if (emails.length === 0 && prev.length === 0) {
					return [{ id: `custom-${Date.now()}`, email: "", selected: true }];
				}
				return prev.map((ce) => ({ ...ce, selected: true }));
			});
		} else {
			setSelectedEmails(new Set());
			setCustomEmails((prev) => prev.map((ce) => ({ ...ce, selected: false })));
		}
	};

	const handleEmailToggle = (email: string) => {
		const newSelected = new Set(selectedEmails);
		if (newSelected.has(email)) {
			newSelected.delete(email);
		} else {
			newSelected.add(email);
		}
		setSelectedEmails(newSelected);
		updateNotifyByEmailState(newSelected, customEmails);
	};

	const handleAddEmail = () => {
		const newEmail: CustomEmail = {
			id: `custom-${Date.now()}`,
			email: "",
			selected: true,
		};
		setCustomEmails((prev) => [...prev, newEmail]);
	};

	const handleCustomEmailToggle = (id: string) => {
		setCustomEmails((prev) =>
			prev.map((ce) => (ce.id === id ? { ...ce, selected: !ce.selected } : ce)),
		);
		const updated = customEmails.map((ce) =>
			ce.id === id ? { ...ce, selected: !ce.selected } : ce,
		);
		updateNotifyByEmailState(selectedEmails, updated);
	};

	const handleConfirm = () => {
		if (!notifyByEmail) {
			onConfirm([]);
			onClose();
			return;
		}

		const allSelectedEmails = [
			...Array.from(selectedEmails),
			...customEmails
				.filter((ce) => ce.selected && ce.email.trim())
				.map((ce) => ce.email.trim()),
		];
		const trimmedNote = publisherNote.trim();
		onConfirm(allSelectedEmails, trimmedNote || undefined);
		onClose();
	};

	useEffect(() => {
		if (open) {
			setCustomEmails([]);
			setHiddenEmails(new Set());
			setExpanded(true);
			setPublisherNote("");
		}
		if (isSuccess && emails.length > 0) {
			setSelectedEmails(new Set(emails));
			setNotifyByEmail(true);
		} else {
			setNotifyByEmail(false);
		}
	}, [emails, isSuccess, open]);

	useEffect(() => {
		if (e && isError) {
			setError((e as Error).message);
		}
	}, [isError, e]);

	const hasInvalidEmails = customEmails.some(
		(ce) => ce.selected && ce.email.trim() !== "" && !isValidEmail(ce.email),
	);

	return (
		<>
			{open && <HelmetDexteel title={title} />}
			<MesfModal
				maxWidth="sm"
				fullWidth
				open={open}
				handleClose={onClose}
				title={title}
			>
				<MesfModal.Content dividers>
					<Typography variant="body2" color="textSecondary">
						{message}
					</Typography>

					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							p: 1,
							pl: 3,
							mt: 2,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								cursor: notifyByEmail ? "pointer" : "default",
							}}
							onClick={notifyByEmail ? () => setExpanded(!expanded) : undefined}
						>
							<FormControlLabel
								control={
									<Switch
										checked={notifyByEmail}
										onChange={(e) => {
											e.stopPropagation();
											handleNotifyToggle();
										}}
										onClick={(e) => e.stopPropagation()}
										color="success"
										slotProps={{
											input: {
												"data-test-id": "notify-by-email-switch",
											} as React.InputHTMLAttributes<HTMLInputElement>,
										}}
									/>
								}
								label={
									<Typography variant="body1" fontWeight={500}>
										Notify users by Email
									</Typography>
								}
								onClick={(e) => e.stopPropagation()}
							/>
							<Box sx={{ flexGrow: 1 }} />
							{expanded ? (
								<ExpandLess
									sx={{
										color: notifyByEmail ? "inherit" : "action.disabled",
									}}
								/>
							) : (
								<ExpandMore
									sx={{
										color: notifyByEmail ? "inherit" : "action.disabled",
									}}
								/>
							)}
						</Box>

						<Collapse in={expanded && notifyByEmail}>
							<Box sx={{ pl: 2, pt: 1 }}>
								{isLoadingEmails ? (
									<Box
										sx={{ display: "flex", justifyContent: "center", py: 2 }}
									>
										<CircularProgress size={24} />
									</Box>
								) : (
									<>
										{emails
											.filter((email) => !hiddenEmails.has(email))
											.map((email) => (
												<Box
													key={email}
													sx={{
														display: "flex",
														alignItems: "center",
														border: "1px solid",
														borderColor: "divider",
														borderRadius: 1,
														mb: 1,
														px: 1,
														userSelect: "none",
													}}
												>
													<Checkbox
														checked={selectedEmails.has(email)}
														onChange={() => handleEmailToggle(email)}
														color="success"
													/>
													<Typography variant="body2" sx={{ flex: 1 }}>
														{email}
													</Typography>
													<Typography
														variant="caption"
														color="text.disabled"
														fontSize={11}
													>
														On last publication
													</Typography>
													<IconButton
														size="small"
														onClick={() => {
															const newHidden = new Set(hiddenEmails);
															newHidden.add(email);
															setHiddenEmails(newHidden);

															const newSelected = new Set(selectedEmails);
															newSelected.delete(email);
															setSelectedEmails(newSelected);
															updateNotifyByEmailState(
																newSelected,
																customEmails,
															);
														}}
														sx={{ color: "text.secondary" }}
													>
														<Close fontSize="small" />
													</IconButton>
												</Box>
											))}

										{customEmails.map((customEmail) => {
											const hasError =
												customEmail.email.trim() !== "" &&
												!isValidEmail(customEmail.email);
											return (
												<Box
													key={customEmail.id}
													sx={{
														display: "flex",
														alignItems: "center",
														border: "1px solid",
														borderColor: hasError ? "error.main" : "divider",
														borderRadius: 1,
														mb: 1,
														px: 1,
														userSelect: "none",
													}}
												>
													<Checkbox
														checked={customEmail.selected && !hasError}
														onChange={() =>
															handleCustomEmailToggle(customEmail.id)
														}
														disabled={hasError}
														color="success"
													/>
													<InputBase
														placeholder="Type new email"
														value={customEmail.email}
														onChange={(e) =>
															setCustomEmails((prev) =>
																prev.map((ce) =>
																	ce.id === customEmail.id
																		? { ...ce, email: e.target.value }
																		: ce,
																),
															)
														}
														sx={{
															flex: 1,
															fontSize: "0.875rem",
															color: hasError ? "error.main" : "inherit",
															"& input::placeholder": {
																color: "text.disabled",
																opacity: 1,
															},
														}}
													/>
													{hasError && (
														<Typography
															variant="caption"
															color="error"
															sx={{ mr: 1 }}
														>
															Invalid
														</Typography>
													)}
													<IconButton
														size="small"
														onClick={() =>
															setCustomEmails((prev) =>
																prev.filter((ce) => ce.id !== customEmail.id),
															)
														}
														sx={{ color: "text.secondary" }}
													>
														<Close fontSize="small" />
													</IconButton>
												</Box>
											);
										})}

										<Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
											<Button
												startIcon={<Add />}
												onClick={handleAddEmail}
												size="small"
											>
												Add email
											</Button>
											{emails.length > 0 && hiddenEmails.size > 0 && (
												<Button
													startIcon={<Restore />}
													onClick={() => {
														setHiddenEmails(new Set());
														setSelectedEmails(new Set(emails));
													}}
													size="small"
												>
													Reset to last emails
												</Button>
											)}
										</Box>
									</>
								)}
							</Box>
						</Collapse>
					</Box>

					<Box sx={{ mt: 2 }}>
						<TextField
							label="Note"
							placeholder="Add any context about this publish that should go in the notification email."
							value={publisherNote}
							onChange={(e) => setPublisherNote(e.target.value)}
							multiline
							minRows={3}
							maxRows={6}
							fullWidth
							size="small"
							disabled={!notifyByEmail || loading}
							helperText={
								notifyByEmail
									? "Included in the body of the notification email."
									: "Enable email notification to add a note."
							}
							slotProps={{
								htmlInput: { "data-test-id": "publisher-note-input" },
							}}
						/>
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						variant="contained"
						color="primary"
						disabled={loading || isLoadingEmails || hasInvalidEmails}
						data-test-id="confirm-publish-emails-button"
					>
						{loading ? "Publishing..." : "Publish"}
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
